import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Check, Loader2 } from "lucide-react";
import { contact, brand } from "../data/siteContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    projectType: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState("");

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
    setServerError("");
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Συμπληρώστε το ονοματεπώνυμο";
    if (!form.phone.trim()) e.phone = "Συμπληρώστε το τηλέφωνο";
    else if (!/^[0-9+\s()-]{8,}$/.test(form.phone.trim()))
      e.phone = "Μη έγκυρο τηλέφωνο";
    if (!form.email.trim()) e.email = "Συμπληρώστε το email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Μη έγκυρο email";
    if (!form.projectType) e.projectType = "Επιλέξτε τύπο έργου";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Γράψτε τουλάχιστον 10 χαρακτήρες";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate() || sending) return;
    setSending(true);
    setServerError("");
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        project_type: form.projectType,
        message: form.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setServerError(
        "Παρουσιάστηκε πρόβλημα κατά την αποστολή. Δοκιμάστε ξανά ή καλέστε μας στο " +
          brand.phoneDisplay +
          "."
      );
    } finally {
      setSending(false);
    }
  };

  const onReset = () => {
    setSubmitted(false);
    setForm({ name: "", phone: "", email: "", projectType: "", message: "" });
  };

  const infoCards = [
    {
      icon: Phone,
      label: "Τηλέφωνα",
      lines: brand.phones.map((p) => ({
        text: p.display,
        href: `tel:${p.number}`,
        tag: p.label,
      })),
    },
    {
      icon: Mail,
      label: "Email",
      lines: [{ text: brand.email, href: `mailto:${brand.email}` }],
    },
    {
      icon: MapPin,
      label: "Διεύθυνση",
      lines: [
        { text: brand.address },
        { text: brand.area, muted: true },
      ],
    },
    {
      icon: Clock,
      label: "Ωράριο Showroom",
      lines: [{ text: brand.hours }],
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="container mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-16">
          <p className="text-xs tracking-[0.35em] uppercase text-brand-red mb-5 font-medium">
            <span className="inline-block w-10 h-px bg-brand-red align-middle mr-4" />
            {contact.eyebrow}
          </p>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl text-brand-charcoal leading-[1.1] tracking-tight mb-6">
            {contact.title}
          </h2>
          <p className="text-base md:text-lg text-neutral-600 leading-relaxed">
            {contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Info cards */}
          <div className="lg:col-span-5 space-y-4">
            {infoCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <div
                  key={i}
                  data-testid={`contact-info-${i}`}
                  className="group flex items-start gap-5 p-6 border border-brand-greyBorder hover:border-brand-red transition-colors"
                >
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center border border-brand-red text-brand-red group-hover:bg-brand-red group-hover:text-white transition-colors">
                    <Icon size={18} strokeWidth={1.6} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 font-semibold">
                      {card.label}
                    </p>
                    <div className="space-y-1">
                      {card.lines.map((ln, j) =>
                        ln.href ? (
                          <a
                            key={j}
                            href={ln.href}
                            className="flex items-center gap-2 text-brand-charcoal hover:text-brand-red transition-colors text-base font-medium"
                          >
                            {ln.text}
                            {ln.tag && (
                              <span className="text-[9px] tracking-widest uppercase text-neutral-400 font-normal">
                                {ln.tag}
                              </span>
                            )}
                          </a>
                        ) : (
                          <p
                            key={j}
                            className={`text-base ${
                              ln.muted ? "text-neutral-500 text-sm" : "text-brand-charcoal font-medium"
                            }`}
                          >
                            {ln.text}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-brand-red bg-brand-grey p-10 md:p-14 text-center"
              >
                <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center border border-brand-red text-brand-red">
                  <Check size={26} strokeWidth={2} />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-4">
                  Ευχαριστούμε!
                </h3>
                <p className="text-base text-neutral-700 leading-relaxed max-w-lg mx-auto mb-8">
                  Το αίτημά σας στάλθηκε επιτυχώς στο{" "}
                  <strong>{brand.email}</strong>. Η ομάδα της Alumak θα
                  επικοινωνήσει μαζί σας εντός 24 ωρών.
                </p>
                <button
                  onClick={onReset}
                  data-testid="contact-reset"
                  className="border border-brand-charcoal hover:bg-brand-charcoal hover:text-white text-brand-charcoal px-8 py-4 text-xs font-semibold tracking-widest uppercase transition-colors"
                >
                  Νέο Αίτημα
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-brand-grey p-8 md:p-12 space-y-6"
                noValidate
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <Field
                    label="Ονοματεπώνυμο"
                    testId="input-name"
                    value={form.name}
                    onChange={(v) => update("name", v)}
                    error={errors.name}
                  />
                  <Field
                    label="Τηλέφωνο"
                    testId="input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(v) => update("phone", v)}
                    error={errors.phone}
                  />
                </div>

                <Field
                  label="Email"
                  testId="input-email"
                  type="email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  error={errors.email}
                />

                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 font-semibold">
                    Τύπος Έργου
                  </label>
                  <select
                    data-testid="input-project-type"
                    value={form.projectType}
                    onChange={(e) => update("projectType", e.target.value)}
                    className="w-full bg-white border border-brand-greyBorder focus:border-brand-red focus:outline-none text-brand-charcoal text-base px-4 py-4"
                  >
                    <option value="">Επιλέξτε...</option>
                    {contact.projectTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  {errors.projectType && (
                    <p
                      data-testid="error-project-type"
                      className="mt-2 text-xs text-brand-red"
                    >
                      {errors.projectType}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 font-semibold">
                    Περιγράψτε τι χρειάζεστε
                  </label>
                  <textarea
                    data-testid="input-message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="w-full bg-white border border-brand-greyBorder focus:border-brand-red focus:outline-none text-brand-charcoal text-base px-4 py-4 resize-none"
                    placeholder="Σύντομη περιγραφή του έργου, διαστάσεις, χρονοδιάγραμμα..."
                  />
                  {errors.message && (
                    <p
                      data-testid="error-message"
                      className="mt-2 text-xs text-brand-red"
                    >
                      {errors.message}
                    </p>
                  )}
                </div>

                {serverError && (
                  <p
                    data-testid="server-error"
                    className="text-sm text-brand-red border border-brand-red/40 bg-white px-4 py-3"
                  >
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit"
                  className="w-full sm:w-auto bg-brand-red hover:bg-brand-redHover disabled:opacity-70 text-white px-10 py-4 text-xs font-semibold tracking-widest uppercase transition-colors inline-flex items-center justify-center gap-3"
                >
                  {sending && <Loader2 size={16} className="animate-spin" />}
                  {sending ? "Αποστολή..." : "Αποστολή Αιτήματος"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, error, testId, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 font-semibold">
        {label}
      </label>
      <input
        type={type}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-brand-greyBorder focus:border-brand-red focus:outline-none text-brand-charcoal text-base px-4 py-4"
      />
      {error && (
        <p
          data-testid={`error-${testId}`}
          className="mt-2 text-xs text-brand-red"
        >
          {error}
        </p>
      )}
    </div>
  );
}
